import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { CARD_CRYPTODRAFT_PAY_SETTLEMENT } from '../../mutations';
import { cryptodraftScreens, forceResetOptions, forceUpdateOptions } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_FC_SETTLE } from '../../mixpanel-events';

const SettleCryptodraft = () => {
  const { t: translate } = useTranslation();

  const [openConfirm, setOpenConfirm] = useState(false);

  const { state, openScreen, forceUpdateAction, forceResetAction } = useContext(CryptodraftContext);

  const cardCryptodraftDetails = state?.cryptodraftDetails;
  const principalCurrency = state?.principalCurrency;
  const fiatCurrency = state?.fiatCurrency;
  const coin = state?.selectedSettlementWallet?.coin;
  const availableBalance = state?.selectedSettlementWallet?.available_balance;
  const cryptodraftSettlementDetails = state?.cryptodraftSettlementDetails;

  const screenOptions = state?.screenOptions;

  const validBalance =
    formatNumber(availableBalance * currencies[coin?.toUpperCase()].conversion) > 0;
  const rate = useRate(coin, fiatCurrency);

  const collateralPrincipleCurrencyToDisplayCurrencyRate = useRate(
    cardCryptodraftDetails?.principal_currency,
    principalCurrency
  );

  const { mutate: payFullSettlement, isLoading } = useMutation(CARD_CRYPTODRAFT_PAY_SETTLEMENT, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        sendStats(MXP_FC_SETTLE, data, FLOWS.FC);
        showAlert({
          message: translate('cryptodraft_full_settlement_success_notification_text'),
          type: 'success',
        });
        forceUpdateAction(forceUpdateOptions.DETAILS, true);
        openScreen(cryptodraftScreens.WITHDRAW);
      }
    },
  });

  const notValidBalanceForFullPayment =
    availableBalance * currencies[coin?.toUpperCase()].conversion * rate <
    cryptodraftSettlementDetails?.total_due;

  useEffect(() => {
    forceUpdateAction(forceUpdateOptions.SETTLEMENT_DETAILS, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screenOptions?.confirmOpened) {
      setOpenConfirm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderOptions = () =>
    !openConfirm ? (
      <div className="btn-container">
        <button
          type="button"
          className="btn border text-white border-shadow py-3 w-100"
          onClick={() => {
            forceResetAction(forceResetOptions.SETTLEMENT_MAIN_WALLET, true);
            openScreen(cryptodraftScreens.PAY_PARTIAL);
          }}>
          {translate('pay_partial_btn_title')}
        </button>
        <button
          type="button"
          className="btn btn-primary py-3 w-100"
          onClick={() => {
            forceResetAction(forceResetOptions.SETTLEMENT_MAIN_WALLET, true);
            setOpenConfirm(true);
          }}>
          {translate('pay_now_btn_title')}
        </button>
      </div>
    ) : (
      <div className="btn-container">
        <button
          type="button"
          loading={isLoading ? 'true' : 'false'}
          disabled={!validBalance || notValidBalanceForFullPayment}
          className="btn btn-primary py-3 w-100"
          onClick={() => {
            payFullSettlement({ coin });
          }}>
          {translate('pay_now_btn_title')}
        </button>
      </div>
    );
  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() =>
        !openConfirm ? openScreen(cryptodraftScreens.DETAILS) : setOpenConfirm(false)
      }
      title={
        !openConfirm
          ? translate('cryptodraft_settlement_title')
          : translate('cryptodraft_settlement_confirm_title')
      }>
      <div className="mt-5">
        <p className="text-f5 text-dark p-0">{translate('cryptodraft_settlement_subtitle')}</p>
      </div>
      <div className="mt-2">
        <b className="text-f4 text-dark p-0">{`${formatNumberWithComma(
          formatNumber(
            cryptodraftSettlementDetails?.total_due *
              collateralPrincipleCurrencyToDisplayCurrencyRate,
            exchange.FIAT_VALUE
          )
        )} ${principalCurrency.toUpperCase()}`}</b>
      </div>
      <div className="mt-4">
        <p className="text-shadow text-f6 p-0">{translate('interest_rate_label')}</p>
      </div>
      <div className="rounded-extra bg-lighter mt-3 card-calculator__summary">
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {translate('cryptodraft_settlement_principle_owed')}
          </p>
          <p className="text-f6">{`${formatNumberWithComma(
            formatNumber(
              cryptodraftSettlementDetails?.principal_owed *
                collateralPrincipleCurrencyToDisplayCurrencyRate,
              exchange.FIAT_VALUE
            )
          )} ${principalCurrency.toUpperCase()}`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between p-3">
          <p className="text-dark text-f6 d-flex align-items-center">
            {translate('cryptodraft_settlement_interest_due')}
          </p>
          <p className="text-f6">{`${formatNumberWithComma(
            formatNumber(
              cryptodraftSettlementDetails?.interest_due *
                collateralPrincipleCurrencyToDisplayCurrencyRate,
              exchange.FIAT_VALUE
            )
          )} ${principalCurrency.toUpperCase()}`}</p>
        </div>
      </div>
      <div className="d-flex flex-row mt-4 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('cryptodraft_settlement_total_cost')}
        </p>
        <span className="text-right flex-fill">
          <b className="text-f6 m-0">
            {`${formatNumberWithComma(
              formatNumber(
                cryptodraftSettlementDetails?.total_due *
                  collateralPrincipleCurrencyToDisplayCurrencyRate,
                exchange.FIAT_VALUE
              )
            )} ${principalCurrency.toUpperCase()}`}
          </b>
        </span>
      </div>
      {openConfirm && (
        <div className="mt-4">
          <div className="">
            <p className="text-f6 text-shadow p-0">
              {translate('cryptodraft_calculator_choose_source')}
            </p>
          </div>
          <div className="select-group bg-lighter rounded-extra p-3 d-flex flex-row mt-2 mb-4 select-currency-container">
            <div
              role="button"
              tabIndex="0"
              className="d-flex flex-row align-items-center px-2"
              onClick={() =>
                openScreen(cryptodraftScreens.WALLETS, { prevScreen: cryptodraftScreens.SETTLE })
              }
              onKeyDown={() =>
                openScreen(cryptodraftScreens.WALLETS, { prevScreen: cryptodraftScreens.SETTLE })
              }>
              <CurrencyIcon currencyCode={coin} className="me-2" />
              <p className="m-0 me-2 text-f5">{coin.toUpperCase()}</p>
              <i className="icon icon-arrow-down-navigation" />
            </div>
            <span className="text-right flex-fill">
              <b className="text-f5 m-0">
                {formatNumberWithComma(
                  formatNumber(availableBalance * currencies[coin.toUpperCase()].conversion)
                )}{' '}
                {coin.toUpperCase()}
              </b>
              <p className="text-shadow text-f6 m-0">
                {formatNumberWithComma(
                  formatNumber(
                    availableBalance * rate * currencies[fiatCurrency].conversion,
                    exchange.FIAT_VALUE
                  )
                )}{' '}
                {fiatCurrency.toUpperCase()}
              </p>
            </span>
          </div>
        </div>
      )}
      {renderOptions()}
    </MainBodyLayout>
  );
};

export default SettleCryptodraft;
